import { create } from "zustand";
import type { ProductExtraSelection, Category } from "@/types";
import type { Area } from "react-easy-crop";

interface ProductFormState {
  // Form state
  extras: ProductExtraSelection[];
  selectedCategory: Category | null;

  // Image state
  imageFile: File | null;
  imagePreviewUrl: string | null;
  croppedAreaPixels: Area | null;
  croppedImageBlob: Blob | null;

  // Actions - Extras
  addExtra: (extra: ProductExtraSelection) => void;
  updateExtra: (index: number, extra: ProductExtraSelection) => void;
  removeExtra: (index: number) => void;
  setExtras: (extras: ProductExtraSelection[]) => void;

  // Actions - Category
  setSelectedCategory: (category: Category | null) => void;

  // Actions - Image
  setImageFile: (file: File | null) => void;
  setImagePreviewUrl: (url: string | null) => void;
  setCroppedAreaPixels: (area: Area | null) => void;
  setCroppedImageBlob: (blob: Blob | null) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  extras: [],
  selectedCategory: null,
  imageFile: null,
  imagePreviewUrl: null,
  croppedAreaPixels: null,
  croppedImageBlob: null,
};

export const useProductFormStore = create<ProductFormState>((set) => ({
  ...initialState,

  // Extras actions
  addExtra: (extra: ProductExtraSelection) => {
    set((state) => ({ extras: [...state.extras, extra] }));
  },

  updateExtra: (index: number, extra: ProductExtraSelection) => {
    set((state) => ({
      extras: state.extras.map((e, i) => (i === index ? extra : e)),
    }));
  },

  removeExtra: (index: number) => {
    set((state) => ({
      extras: state.extras.filter((_, i) => i !== index),
    }));
  },

  setExtras: (extras: ProductExtraSelection[]) => {
    set({ extras });
  },

  // Category actions
  setSelectedCategory: (category: Category | null) => {
    set({ selectedCategory: category });
  },

  // Image actions
  setImageFile: (file: File | null) => {
    set({ imageFile: file });
    if (file) {
      const url = URL.createObjectURL(file);
      set({ imagePreviewUrl: url });
    } else {
      set({ imagePreviewUrl: null });
    }
  },

  setImagePreviewUrl: (url: string | null) => {
    set({ imagePreviewUrl: url });
  },

  setCroppedAreaPixels: (area: Area | null) => {
    set({ croppedAreaPixels: area });
  },

  setCroppedImageBlob: (blob: Blob | null) => {
    set({ croppedImageBlob: blob });
  },

  // Reset
  reset: () => {
    set(initialState);
  },
}));
